import { useCallback, useEffect, useState } from "react";
import {
  downloadDataImportFile,
  getImportRequestStatus,
  ImportRequestModel,
} from "../../clients/BackOfficeApi";
import { Box, Button, Heading, Spinner, Text } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { DownloadIcon } from "@chakra-ui/icons";
import { BackButton } from "../../elements/BackButton";
import { useIsAuthenticated } from "@azure/msal-react";
import { LinkButton } from "../../elements/LinkButton";
import { FormattedDate, FormattedMessage } from "react-intl";

export function ImportStatusPage() {
  const { importId } = useParams();
  const isAuthenticated = useIsAuthenticated();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [importRequestStatus, setImportRequestStatus] =
    useState<ImportRequestModel>();

  useEffect(() => {
    if (!isAuthenticated || !importId) return;

    const abortController = new AbortController();

    setLoading(true);

    getImportRequestStatus({
      path: { id: Number(importId) },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setImportRequestStatus(data);
        }
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        setError(`Fout bij ophalen import status: ${e.message ?? String(e)}`);
      })
      .finally(() => setLoading(false));

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [isAuthenticated, importId]);

  const downloadFile = useCallback(() => {
    if (!importRequestStatus) return;

    return downloadDataImportFile({
      path: { fileReferenceId: importRequestStatus.fileReferenceId },
      throwOnError: true,
    }).then(({ data }) => {
      if (data instanceof Blob || data instanceof MediaSource) {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", importRequestStatus.filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    });
  }, [importRequestStatus]);

  if (loading) return <Spinner />;

  return (
    <Box>
      <Box mb={4}>
        <BackButton />
      </Box>
      {error && <Text color="red.500">{error}</Text>}
      {importRequestStatus && (
        <>
          <Heading>Status van import {importId}</Heading>
          <Text>
            Bestand:{" "}
            <Button onClick={downloadFile} variant="link">
              {importRequestStatus.filename} <DownloadIcon ml={1} />
            </Button>
          </Text>
          <Text>
            Modus:{" "}
            <FormattedMessage
              defaultMessage={importRequestStatus.mode}
              id={`importRequest.mode.${importRequestStatus.mode}`}
            />
          </Text>
          <Text>
            Status:{" "}
            <FormattedMessage
              defaultMessage={importRequestStatus.status}
              id={`importRequest.status.${importRequestStatus.status}`}
            />
          </Text>
          <Text>
            Ingediend op:{" "}
            <FormattedDate
              dateStyle="long"
              timeStyle="short"
              value={importRequestStatus.creationDateTime}
            />
          </Text>
          <Text>
            {!!importRequestStatus.reportId && (
              <LinkButton to="report">Naar rapport</LinkButton>
            )}
          </Text>
        </>
      )}
    </Box>
  );
}
